import { Component } from '@theme/component';

/**
 * A custom element that renders a scroll-position indicator for a horizontally
 * scrolling sibling list.
 *
 * Used by the predictive search modal, where the design calls for a 1px progress
 * line detached from the card grid rather than a native scrollbar (which cannot be
 * offset from its scroll container and is only styleable in Chromium).
 *
 * The element hides itself when the target does not overflow, so it never shows on
 * mobile or when there are too few results to scroll.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} thumb - The moving part of the indicator.
 *
 * @extends Component<Refs>
 */
class ScrollProgressComponent extends Component {
  requiredRefs = ['thumb'];

  /** @type {HTMLElement | null} */
  #scroller = null;

  /** @type {number} */
  #frame = 0;

  #resizeObserver = new ResizeObserver(() => this.#update());

  connectedCallback() {
    super.connectedCallback();

    const selector = this.dataset.scrollTarget;
    this.#scroller = selector ? (this.parentElement?.querySelector(selector) ?? null) : null;

    if (!this.#scroller) {
      this.hidden = true;
      return;
    }

    this.#scroller.addEventListener('scroll', this.#handleScroll, { passive: true });
    this.#resizeObserver.observe(this.#scroller);
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#scroller?.removeEventListener('scroll', this.#handleScroll);
    this.#resizeObserver.disconnect();

    if (this.#frame) cancelAnimationFrame(this.#frame);
    this.#frame = 0;
  }

  /**
   * Coalesces scroll events into a single write per frame.
   */
  #handleScroll = () => {
    if (this.#frame) return;

    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#update();
    });
  };

  /**
   * Sizes and positions the thumb to reflect the visible portion of the scroller.
   */
  #update() {
    const scroller = this.#scroller;
    if (!scroller) return;

    const { clientWidth, scrollWidth } = scroller;
    const overflows = scrollWidth - clientWidth > 1;

    this.hidden = !overflows;
    if (!overflows) return;

    // The track is laid out in the same column as the scroller, so its width matches
    // the scroller's client width and these ratios can be used as percentages directly.
    const size = (clientWidth / scrollWidth) * 100;
    const offset = (Math.abs(scroller.scrollLeft) / scrollWidth) * 100;

    const { thumb } = this.refs;
    thumb.style.setProperty('--scroll-progress-size', `${size}%`);
    thumb.style.setProperty('--scroll-progress-offset', `${offset}%`);
  }
}

if (!customElements.get('scroll-progress')) customElements.define('scroll-progress', ScrollProgressComponent);
